/**
 * Scope 3 Admin Result Dialog
 *
 * Allows admins to:
 * 1. View submission details
 * 2. Download the uploaded file
 * 3. Enter the calculated tCO2e result
 * 4. Add notes about the calculation
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Download,
  Close,
  CheckCircle,
} from '@mui/icons-material';
import {
  scope3SubmissionService,
  Scope3Submission,
} from '../../services/scope3SubmissionService';

interface Scope3AdminResultDialogProps {
  open: boolean;
  submission: Scope3Submission | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const Scope3AdminResultDialog: React.FC<Scope3AdminResultDialogProps> = ({
  open,
  submission,
  onClose,
  onSuccess,
}) => {
  const [resultTco2e, setResultTco2e] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when submission changes
  useEffect(() => {
    if (submission) {
      setResultTco2e(submission.result_tco2e?.toString() || '');
      setNotes(submission.result_notes || '');
      setError(null);
    }
  }, [submission]);

  const handleDownloadFile = async () => {
    if (!submission) return;

    setIsDownloading(true);
    try {
      const { url, filename } = await scope3SubmissionService.downloadSubmissionFile(submission.id);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async () => {
    if (!submission) return;

    const tco2eValue = parseFloat(resultTco2e);
    if (isNaN(tco2eValue) || tco2eValue < 0) {
      setError('Please enter a valid positive number for tCO2e');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await scope3SubmissionService.submitResult(submission.id, {
        result_tco2e: tco2eValue,
        result_notes: notes || undefined,
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit result');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!submission) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Enter Calculation Result
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Submission Details */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Submission Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body2">
                {submission.category_number}. {submission.category_name}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">
                Facility
              </Typography>
              <Typography variant="body2">{submission.facility_name}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">
                Period
              </Typography>
              <Typography variant="body2">{submission.reporting_period}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Uploaded File
              </Typography>
              <Typography variant="body2">
                {submission.original_filename} ({formatFileSize(submission.file_size_bytes)})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Uploaded: {formatDate(submission.created_at)}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={isDownloading ? <CircularProgress size={16} /> : <Download />}
              onClick={handleDownloadFile}
              disabled={isDownloading}
            >
              Download
            </Button>
          </Box>
        </Paper>

        {/* Instructions */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Download the file, perform the emissions calculation externally, and enter the
            total tCO2e result below.
          </Typography>
        </Alert>

        {/* Result Input */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Total Emissions (tCO2e)"
            type="number"
            value={resultTco2e}
            onChange={(e) => setResultTco2e(e.target.value)}
            fullWidth
            required
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Enter the calculated total emissions in tonnes CO2 equivalent"
          />

          <TextField
            label="Calculation Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            helperText="Optional: Add any notes about the calculation methodology or data quality"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !resultTco2e}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
          color="success"
        >
          {isLoading ? 'Submitting...' : 'Submit Result'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
